import { useCallback, useReducer } from "react";
import type {
  ConnectionHubError,
  ConnectionHubMode,
  ConnectionSocketPayload,
  InboxGroup,
  ResolveResult,
  SetupResponse,
  StandaloneCard,
} from "../api/connectionHub.types";
import { CONNECTION_HUB_MODE } from "../api/connectionHub.types";
import { CONNECTION_STATUS } from "../constants/connectionStatus.constants";
import { connectionHubApi } from "../api/connectionHub.api";
import { isConnectionHubError } from "../utils/handleConnectionError";

export const CONNECT_FLOW_STATUS = {
  IDLE: "idle",
  RESOLVING: "resolving",
  RESOLVED: "resolved",
  FAILED: "failed",
} as const;
export type ConnectFlowStatus = typeof CONNECT_FLOW_STATUS[keyof typeof CONNECT_FLOW_STATUS];

/** Which connection the edit step should open, and which branch to show. Beats the resolve-time editContext. */
export interface EditTarget {
  connectionId: string;
  type: "credentials" | "inboxRecheck";
  inboxId?: string | null;
}

export type ConnectFlowStep = "accounts" | "credentials" | "finishSetup" | "subBrands" | "edit" | "delegatedView";

export interface ConnectFlowLaunch {
  launchId: number;
  ipIds: string[];
  mode: ConnectionHubMode;
  agencyClientId: number | null;
  connectionId?: string;
  startNew?: boolean;
  afterCredentials?: boolean;
}

export interface ConnectFlowState extends ConnectFlowLaunch {
  status: ConnectFlowStatus;
  step: ConnectFlowStep | null;
  result: ResolveResult | null;
  error: ConnectionHubError | null;
  activeIpId: string | null;
  activeEdit: EditTarget | null;
}

const initialState: ConnectFlowState = {
  launchId: 0,
  ipIds: [],
  mode: "single",
  agencyClientId: null,
  status: CONNECT_FLOW_STATUS.IDLE,
  step: null,
  result: null,
  error: null,
  activeIpId: null,
  activeEdit: null,
};

export const isSetupComplete = (result: SetupResponse): boolean =>
  (result.inboxes.length > 0 || result.standalone.length > 0) &&
  result.inboxes.every((group) => group.status === CONNECTION_STATUS.CONNECTED) &&
  result.standalone.every((card) => card.status === CONNECTION_STATUS.CONNECTED);

export const hasPendingSubBrands = (result: SetupResponse): boolean =>
  result.inboxes.some((group) => group.covers.some((cover) => cover.pendingSubBrands.length > 0)) ||
  result.standalone.some((card) => card.connection.pendingSubBrands.length > 0);

const replaceGroup = (result: ResolveResult, group: InboxGroup): ResolveResult => {
  const index = result.inboxes.findIndex((item) => item.inbox.id === group.inbox.id);
  if (index < 0) return { ...result, inboxes: [...result.inboxes, group] };
  const inboxes = [...result.inboxes];
  inboxes[index] = group;
  return { ...result, inboxes };
};

const replaceStandalone = (result: ResolveResult, card: StandaloneCard): ResolveResult => {
  const index = result.standalone.findIndex((item) => item.connection.connectionId === card.connection.connectionId);
  if (index < 0) return { ...result, standalone: [...result.standalone, card] };
  const standalone = [...result.standalone];
  standalone[index] = card;
  return { ...result, standalone };
};

const mergeCard = (result: ResolveResult, card: InboxGroup | StandaloneCard): ResolveResult =>
  "inbox" in card ? replaceGroup(result, card) : replaceStandalone(result, card);

const knownConnectionIds = (result: ResolveResult): Set<string> =>
  new Set([
    ...result.inboxes.flatMap((group) => group.covers.map((cover) => cover.connectionId)),
    ...result.standalone.map((card) => card.connection.connectionId),
  ]);

const mergeSocket = (state: ConnectFlowState, payload: ConnectionSocketPayload): ResolveResult | null => {
  if (!state.result) return null;
  const known = knownConnectionIds(state.result);
  let result = state.result;
  (payload.inboxGroups ?? []).forEach((group) => {
    const belongs = result.inboxes.some((item) => item.inbox.id === group.inbox.id) ||
      group.covers.some((cover) => known.has(cover.connectionId));
    if (belongs) result = replaceGroup(result, group);
  });
  (payload.standalone ?? []).forEach((card) => {
    if (known.has(card.connection.connectionId)) result = replaceStandalone(result, card);
  });
  return result;
};

type ConnectFlowAction =
  | { type: "resolveStarted"; launch: ConnectFlowLaunch }
  | { type: "resolveSucceeded"; launchId: number; result: ResolveResult }
  | { type: "resolveFailed"; launchId: number; error: ConnectionHubError }
  | { type: "setupCompleted"; setup: SetupResponse }
  | { type: "cardUpdated"; card: InboxGroup | StandaloneCard }
  | { type: "socketPatched"; payload: ConnectionSocketPayload }
  | { type: "replaceIpIds"; ipIds: string[] }
  | { type: "goToStep"; step: ConnectFlowStep; ipId?: string | null }
  | { type: "goToEdit"; target: EditTarget }
  | { type: "reset" };

const connectFlowReducer = (state: ConnectFlowState, action: ConnectFlowAction): ConnectFlowState => {
  switch (action.type) {
    case "resolveStarted":
      return { ...action.launch, status: CONNECT_FLOW_STATUS.RESOLVING, step: null, result: null, error: null, activeIpId: null, activeEdit: null };
    case "resolveSucceeded": {
      if (action.launchId !== state.launchId) return state;
      if (state.mode === CONNECTION_HUB_MODE.MULTI && !state.afterCredentials) {
        return {
          ...state,
          status: CONNECT_FLOW_STATUS.RESOLVED,
          step: "credentials",
          result: { ...action.result, startStep: "credentials", channelsNeedingCredentials: state.ipIds, inboxes: [], standalone: [] },
          error: null,
          activeIpId: null,
          activeEdit: null,
        };
      }
      const step = action.result.startStep === "finishSetup" && hasPendingSubBrands(action.result)
        ? "subBrands"
        : action.result.startStep;
      return {
        ...state,
        status: CONNECT_FLOW_STATUS.RESOLVED,
        step,
        result: action.result,
        error: null,
        activeIpId: null,
        activeEdit: null,
      };
    }
    case "resolveFailed":
      return action.launchId === state.launchId ? { ...state, status: CONNECT_FLOW_STATUS.FAILED, error: action.error } : state;
    case "setupCompleted": {
      const result: ResolveResult = {
        ...action.setup,
        startStep: "finishSetup",
        channelsNeedingCredentials: [],
        drafts: [],
        editContext: null,
      };
      return {
        ...state,
        status: CONNECT_FLOW_STATUS.RESOLVED,
        step: hasPendingSubBrands(result) ? "subBrands" : "finishSetup",
        result,
        error: null,
        activeIpId: null,
        activeEdit: null,
      };
    }
    case "cardUpdated": {
      if (!state.result) return state;
      const result = mergeCard(state.result, action.card);
      return {
        ...state,
        result,
        step: hasPendingSubBrands(result) ? "subBrands" : "finishSetup",
        error: null,
      };
    }
    case "socketPatched": {
      const result = mergeSocket(state, action.payload);
      if (!result) return state;
      const step = state.step === "credentials" || state.step === "accounts" || state.step === "edit" || state.step === "delegatedView"
        ? state.step
        : hasPendingSubBrands(result) ? "subBrands" : "finishSetup";
      return { ...state, result, step };
    }
    case "replaceIpIds":
      return { ...state, ipIds: action.ipIds };
    case "goToStep":
      return { ...state, step: action.step, activeIpId: action.ipId ?? null, activeEdit: null, error: null };
    case "goToEdit":
      return { ...state, step: "edit", activeEdit: action.target, error: null };
    case "reset":
      return initialState;
    default:
      return state;
  }
};

export const useConnectFlow = () => {
  const [state, dispatch] = useReducer(connectFlowReducer, initialState);

  const resolve = useCallback(async (launch: ConnectFlowLaunch): Promise<void> => {
    dispatch({ type: "resolveStarted", launch });
    if (launch.startNew) {
      const drafts = await connectionHubApi.resolveSetup(launch.ipIds, launch.agencyClientId)
        .then((result) => result.drafts)
        .catch(() => []);
      dispatch({
        type: "resolveSucceeded",
        launchId: launch.launchId,
        result: {
          startStep: "credentials",
          editContext: null,
          channelsNeedingCredentials: launch.ipIds,
          drafts,
          inboxes: [],
          standalone: [],
        },
      });
      return;
    }
    try {
      const result = await connectionHubApi.resolveSetup(launch.ipIds, launch.agencyClientId, launch.connectionId);
      dispatch({ type: "resolveSucceeded", launchId: launch.launchId, result });
    } catch (error) {
      if (isConnectionHubError(error)) dispatch({ type: "resolveFailed", launchId: launch.launchId, error });
    }
  }, []);

  const retry = useCallback((): void => {
    if (state.launchId) void resolve({ launchId: state.launchId, ipIds: state.ipIds, mode: state.mode, agencyClientId: state.agencyClientId, connectionId: state.connectionId, startNew: state.startNew, afterCredentials: state.afterCredentials });
  }, [resolve, state.afterCredentials, state.agencyClientId, state.connectionId, state.ipIds, state.launchId, state.mode, state.startNew]);

  const completeCredentials = useCallback((setup: SetupResponse): void => {
    if (!state.launchId) return;
    if (state.startNew) {
      dispatch({ type: "setupCompleted", setup });
      return;
    }
    void resolve({
      launchId: state.launchId,
      ipIds: state.ipIds,
      mode: state.mode,
      agencyClientId: state.agencyClientId,
      afterCredentials: true,
    });
  }, [resolve, state.agencyClientId, state.ipIds, state.launchId, state.mode, state.startNew]);

  return {
    state,
    resolve,
    retry,
    goToStep: useCallback((step: ConnectFlowStep, ipId?: string | null) => dispatch({ type: "goToStep", step, ipId }), []),
    goToEdit: useCallback((target: EditTarget) => dispatch({ type: "goToEdit", target }), []),
    completeCredentials,
    applyCardUpdate: useCallback((card: InboxGroup | StandaloneCard) => dispatch({ type: "cardUpdated", card }), []),
    applySocketUpdate: useCallback((payload: ConnectionSocketPayload) => dispatch({ type: "socketPatched", payload }), []),
    replaceIpIds: useCallback((ipIds: string[]) => dispatch({ type: "replaceIpIds", ipIds }), []),
    reset: useCallback(() => dispatch({ type: "reset" }), []),
  };
};
